import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { FormSubmitButton } from '../../../../components/Button/FormSubmitButton';
import EmptyState from '../../../../components/Cards/EmptyState';
import PlusIcon from '../../../../components/Icons/PlusIcon';
import ThirdPartyAccessIcon from '../../../../components/Icons/ThirdPartyAccessIcon';
import { Modal } from '../../../../components/Modal/Modal';
import AddThirdPartyAccessModal from '../../../../components/Modal/ModalActions/AddThirdPartyAccessModal/AddThirdPartyAccessModal';
import Table from '../../../../components/Table/Table';
import { TableLoadingSkeleton } from '../../../../components/Table/TableComponent/TableLoadingSkeleton';
import useGetThirdPartyAccess from '../../../../hooks/dashboard/settings/useGetThirdPartyAccess';
import {
  ExpiryDateCell,
  RegenerateTokenCell,
  StatusBadgeCell,
} from './ThirdPartyAccessCells';

const ThirdPartyAPI = () => {
  const [showModal, setShowModal] = useState(false);
  // Bumping this remounts the table so it refetches from scratch
  const [tableKey, setTableKey] = useState(0);
  const { getThirdPartyAccess, thirdPartyAccessList, loading } =
    useGetThirdPartyAccess();

  useEffect(() => {
    getThirdPartyAccess();
  }, [getThirdPartyAccess]);

  const handleAddSuccess = () => {
    getThirdPartyAccess();
    setTableKey((prevKey) => prevKey + 1);
  };

  const addThirdPartyButton = (
    <FormSubmitButton
      className='fit'
      btnType='button'
      onClick={() => setShowModal(true)}
    >
      <PlusIcon />
      Add Third Party
    </FormSubmitButton>
  );

  const renderBody = () => {
    if (loading && thirdPartyAccessList.length === 0) {
      return (
        <ContentBox className='padded'>
          <TableLoadingSkeleton />
        </ContentBox>
      );
    }

    if (thirdPartyAccessList.length === 0) {
      return (
        <ContentBox>
          <EmptyState
            label='No third-party access yet'
            icon={
              <EmptyIconWrapper>
                <ThirdPartyAccessIcon />
              </EmptyIconWrapper>
            }
            optionalComp={
              <EmptyStateBody>
                <span>
                  Add a partner and they'll get their own key to use — so you
                  never have to hand over yours.
                </span>
                {addThirdPartyButton}
              </EmptyStateBody>
            }
          />
        </ContentBox>
      );
    }

    return (
      <ContentBox className='scroll-x'>
        <Table
          key={tableKey}
          disableMobileComp
          showPaginationControl={false}
          tableDataSourceUrl='api/v1/partner-api-keys'
          addRowBorder
          stickyHeader
          height={360}
          headerBgColor='#FAFAFA'
          firstColumnOffset='20px'
          tableHeader={{
            Name: 'partnerName',
            'Contact email': 'partnerEmail',
            'Date of expiry': 'expiresAt',
            Status: 'status',
            ' ': 'action',
          }}
          tableColumnWidth={{
            Name: '1.2fr',
            'Contact email': '1.4fr',
            'Date of expiry': '1fr',
            Status: '0.8fr',
            ' ': 'auto',
          }}
          cellsCustomComp={{
            expiresAt: <ExpiryDateCell />,
            status: <StatusBadgeCell />,
            action: <RegenerateTokenCell />,
          }}
        />
      </ContentBox>
    );
  };

  return (
    <Container>
      <HeaderRow>
        <Header>
          <HeaderLabel>Third-party APIs</HeaderLabel>
          <HeaderDescription>
            Give a partner their own scoped key instead of sharing yours. They
            generate it themselves from a link you send.
          </HeaderDescription>
        </Header>
        {addThirdPartyButton}
      </HeaderRow>

      {renderBody()}

      <Modal showModal={showModal} setShowModal={setShowModal}>
        <AddThirdPartyAccessModal onSuccess={handleAddSuccess} />
      </Modal>
    </Container>
  );
};

export default ThirdPartyAPI;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 28px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 560px;
`;

const HeaderLabel = styled.div`
  font-size: 18px;
  line-height: 22px;
  font-weight: 600;
`;

const HeaderDescription = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const ContentBox = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  background-color: #fff;
  overflow: hidden;

  &.padded {
    padding: 24px;
  }

  &.scroll-x {
    overflow-x: auto;
  }
`;

const EmptyIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.palette.tertiaryBgColor};
`;

const EmptyStateBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 400px;
  line-height: 20px;
`;
